import {
  ComponentPropsWithoutRef,
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  createContext,
  Dispatch,
  SetStateAction,
  ReactNode,
  type MutableRefObject,
} from "react";
import cx from "classnames";

import {
  useCombobox as useDownshiftCombobox,
  useMultipleSelection as useDownshiftMultipleSelection,
  UseComboboxProps as UseDownshiftComboboxProps,
  UseMultipleSelectionProps as UseDownshiftMultipleSelectionProps,
  UseComboboxReturnValue as UseDownshiftComboboxReturnValue,
  UseMultipleSelectionReturnValue as UseDownshiftMultipleSelectionReturnValue,
  UseComboboxState as UseDownshiftComboboxState,
  UseMultipleSelectionState as UseDownshiftMultipleSelectionState,
  useSelect as useDownshiftSelect,
  UseSelectProps as UseDownshiftSelectProps,
  UseSelectReturnValue as UseDownshiftSelectReturnValue,
  UseSelectState as UseDownshiftSelectState,
} from "downshift";
import { matchSorter, MatchSorterOptions } from "match-sorter";

import { Popover } from "../Popover";

import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

import { ComboboxSearchField, ComboboxSelectTrigger } from "./ComboboxTrigger";
import { ComboboxContent } from "./ComboboxContent";
import { ComboboxList } from "./ComboboxList";
import { ComboboxItem, ComboboxItemAddNew } from "./ComboboxItem";
import { ComboboxEmpty } from "./ComboboxEmpty";
import {
  defaultItemToString,
  defaultItemToKey,
  useDownshiftEnvironment,
  ADD_NEW,
} from "./ComboboxUtils";

import { supportsPopover } from "../../internal/functions/supportsPopover";

import styles from "./Combobox.module.scss";

import {
  ComboboxLegacyMultiple,
  ComboboxLegacySingle,
  SelectLegacyMultiple,
  SelectLegacySingle,
} from "../ComboboxLegacy/ComboboxLegacy";
import { ComboboxGroupByValue } from "./internal/ComboboxGroupContext";
import { useGroups } from "./internal/useGroups";

const DEBUG_STATE_REDUCERS = false;

/**
 * Props for the ComboboxContext component
 * @extends Required<Pick<UseDownshiftComboboxProps<Item> | UseDownshiftSelectProps<Item>, "items" | "itemToString" | "itemToKey" | "selectedItem">>
 * @extends Pick<UseDownshiftComboboxReturnValue<Item> | UseDownshiftSelectReturnValue<Item>, "getToggleButtonProps" | "getItemProps" | "getLabelProps" | "getMenuProps" | "closeMenu" | "selectItem" | "highlightedIndex" | "isOpen" | "inputValue">
 * @extends Partial<Pick<UseDownshiftComboboxReturnValue<Item>, "getInputProps">>
 * @extends Partial<Pick<UseDownshiftMultipleSelectionReturnValue<Item>, "getSelectedItemProps" | "getDropdownProps" | "addSelectedItem" | "removeSelectedItem" | "setSelectedItems" | "reset">>
 * @extends Partial<Pick<UseDownshiftComboboxReturnValue<Item> | UseDownshiftSelectReturnValue<Item>, "reset">>
 * @extends LayoutUtilProps
 */
export type ComboboxContextProps<Item = any> = Required<
  Pick<
    UseDownshiftComboboxProps<Item> | UseDownshiftSelectProps<Item>,
    "items" | "itemToString" | "itemToKey" | "selectedItem"
  >
> &
  Pick<
    UseDownshiftComboboxReturnValue<Item> | UseDownshiftSelectReturnValue<Item>,
    | "getToggleButtonProps"
    | "getItemProps"
    | "getLabelProps"
    | "getMenuProps"
    | "closeMenu"
    | "selectItem"
    | "highlightedIndex"
    | "isOpen"
    | "inputValue"
  > &
  // Select-only doesn't have "getInputProps" so this needs to be made optional
  Partial<Pick<UseDownshiftComboboxReturnValue<Item>, "getInputProps">> &
  Partial<
    Pick<
      UseDownshiftMultipleSelectionReturnValue<Item>,
      | "getSelectedItemProps"
      | "getDropdownProps"
      | "addSelectedItem"
      | "removeSelectedItem"
      | "setSelectedItems"
      | "reset"
    >
  > &
  // Multiple and select-only both have "reset" but single-select doesn't
  Partial<
    Pick<
      | UseDownshiftComboboxReturnValue<Item>
      | UseDownshiftSelectReturnValue<Item>,
      "reset"
    >
  > &
  LayoutUtilProps & {
    disableClearSelection?: boolean;
    isReadOnly?: boolean;
    isDisabled?: boolean;
    /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
    setIsReadOnly: Dispatch<SetStateAction<undefined | boolean>>;
    /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
    setIsDisabled: Dispatch<SetStateAction<undefined | boolean>>;
    hasDisabledPopover: boolean;
    setHasDisabledPopover: Dispatch<SetStateAction<boolean>>;
    hasEmpty: boolean;
    setHasEmpty: Dispatch<SetStateAction<boolean>>;

    multiple: boolean;
    select: boolean;
    selectedItems: Item[];
    disabledItems: Item[];
    setDisabledItems: Dispatch<SetStateAction<Item[]>>;
    setForceCloseOnSelectItems: Dispatch<SetStateAction<Item[]>>;
    setForceClearInputValueOnSelectItems: Dispatch<SetStateAction<Item[]>>;

    loading: boolean;

    referenceRef: MutableRefObject<HTMLDivElement | null>;
    contentRef: MutableRefObject<HTMLDivElement | null>;

    // select-only can't add new so these are optional
    hasAddNew?: boolean;
    setHasAddNew?: Dispatch<SetStateAction<boolean>>;
    onSelectItemAddNew?: (
      inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
    ) => void;
    setOnSelectItemAddNew?: Dispatch<
      SetStateAction<
        | ((
            inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
          ) => void)
        | undefined
      >
    >;
    hasExactMatch?: boolean;
    setHasExactMatch?: Dispatch<SetStateAction<boolean>>;

    groupBy?: string;
    groupToString?: (groupByValue: ComboboxGroupByValue) => string | null;
    groups: {
      key: ComboboxGroupByValue;
      label: string | null;
      items: Item[];
    }[];
  };

export const ComboboxContext = createContext<ComboboxContextProps | null>(null);

/**
 * Hook to access the Combobox context
 * @returns The Combobox context value
 * @throws Error if used outside of a Combobox component
 */
export const useCombobox = function <Item = any>() {
  const context = useContext<ComboboxContextProps<Item> | null>(
    ComboboxContext,
  );
  if (context == null) {
    throw new Error("useCombobox must be wrapped in <Combobox />");
  }
  return context;
};

/**
 * Props for shared functionality across all combobox variants
 * @extends LayoutUtilProps
 * @extends Pick<ComponentPropsWithoutRef<"div">, "className" | "style">
 */
type SharedProps<Item> = LayoutUtilProps &
  Pick<ComponentPropsWithoutRef<"div">, "className" | "style"> & {
    children?: ReactNode;
    /**
     * @default false
     */
    disabled?: boolean;
    /**
     * @default false
     */
    readOnly?: boolean;
    /**
     * @default false
     */
    loading?: boolean;
    /**
     * @default false
     */
    disableCloseOnClickOutside?: boolean;
    /**
     * @default false
     */
    disableMatchReferenceWidth?: boolean;

    /**
     * @deprecated to be removed on next major
     * This is not needed anymore as the Popover API is used.
     * Popover API uses top-layer for its popover content.
     */
    root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;

    /**
     * @default false
     */
    open?: boolean;
    /**
     * @default false
     */
    disableCloseOnSelectItem?: boolean;

    onStateChange?: (
      changes: { type: string } & Partial<UseDownshiftComboboxState<Item>> &
        Partial<UseDownshiftMultipleSelectionState<Item>> &
        Partial<UseDownshiftSelectState<Item>>,
    ) => void;

    minPopoverHeight?: string | number;
    groupBy?: string;
    groupToString?: (groupByValue: ComboboxGroupByValue) => string | null;
    /** Accepts groupBy values `a` and `b` -- must return a negative if `a` before `b` and a positive if `b` before `a`.
     * @default {func} (a, b) => `${a}`.localeCompare(`${b}`)
     */
    groupSorter?: (a: ComboboxGroupByValue, b: ComboboxGroupByValue) => number;
  };

/**
 * Props for combobox-specific functionality
 * @extends Omit<UseDownshiftComboboxProps<Item>, "onStateChange" | "onInputValueChange">
 */
type SharedComboboxProps<Item> = Omit<
  UseDownshiftComboboxProps<Item>,
  "onStateChange" | "onInputValueChange"
> & {
  filterOptions?: MatchSorterOptions<Item>;

  disableFilter?: boolean;

  /**
   * @deprecated Please use `Combobox.SearchField`'s `onChange` method instead
   */
  onInputValueChange?: UseDownshiftComboboxProps<Item>["onInputValueChange"];
};

/**
 * Props for select-specific functionality
 * @extends Omit<UseDownshiftSelectProps<Item>, "onStateChange" | "onInputValueChange">
 */
type SharedSelectProps<Item> = Omit<
  UseDownshiftSelectProps<Item>,
  "onStateChange" | "onInputValueChange"
>;

/**
 * Props for single selection functionality
 */
type SharedSingleProps<Item> = {
  multiple?: never;
  onChange?:
    | Dispatch<SetStateAction<Item | null>>
    | ((arg: Item | null) => void);
};

/**
 * Props for multiple selection functionality
 * @extends Partial<Pick<UseDownshiftMultipleSelectionProps<Item>, "onSelectedItemsChange" | "initialSelectedItems" | "initialActiveIndex" | "defaultSelectedItems" | "defaultActiveIndex" | "activeIndex" | "selectedItems">>
 */
type SharedMultipleProps<Item> = {
  multiple: true;
  onChange?:
    | Dispatch<SetStateAction<Item[] | null>>
    | ((arg: Item[] | null) => void);
} & Partial<
  Pick<
    UseDownshiftMultipleSelectionProps<Item>,
    | "onSelectedItemsChange"
    | "initialSelectedItems"
    | "initialActiveIndex"
    | "defaultSelectedItems"
    | "defaultActiveIndex"
    | "activeIndex"
    | "selectedItems"
  >
>;

/**
 * Props for single select functionality
 * @extends SharedProps<Item>
 * @extends SharedSelectProps<Item>
 * @extends SharedSingleProps<Item>
 */
type SelectSingleProps<Item> = SharedProps<Item> &
  SharedSelectProps<Item> &
  SharedSingleProps<Item> & {
    disableClearSelection?: boolean;
  };

/**
 * Props for multiple select functionality
 * @extends SharedProps<Item>
 * @extends SharedSelectProps<Item>
 * @extends SharedMultipleProps<Item>
 */
type SelectMultipleProps<Item> = SharedProps<Item> &
  SharedSelectProps<Item> &
  SharedMultipleProps<Item>;

/**
 * Props for single combobox functionality
 * @extends SharedProps<Item>
 * @extends SharedComboboxProps<Item>
 * @extends SharedSingleProps<Item>
 */
type ComboboxSingleProps<Item> = SharedProps<Item> &
  SharedComboboxProps<Item> &
  SharedSingleProps<Item>;

/**
 * Props for multiple combobox functionality
 * @extends Omit<SharedProps<Item>, "open" | "disableCloseOnSelectItem">
 * @extends SharedComboboxProps<Item>
 * @extends SharedMultipleProps<Item>
 */
type ComboboxMultipleProps<Item> = Omit<
  SharedProps<Item>,
  "open" | "disableCloseOnSelectItem"
> &
  SharedComboboxProps<Item> &
  SharedMultipleProps<Item> &
  (
    | {
        // the `disableClearInputValueOnSelectItem` prop only makes sense if
        // the popover is being held open by `open={true}` or
        // `disableCloseOnSelectItem={true}`

        disableClearInputValueOnSelectItem: true;
        disableCloseOnSelectItem: true;
        open?: boolean;
      }
    | {
        disableClearInputValueOnSelectItem: true;
        open: true;
        disableCloseOnSelectItem?: boolean;
      }
    | {
        disableClearInputValueOnSelectItem?: never;
        disableCloseOnSelectItem?: boolean;
        open?: boolean;
      }
  );

/**
 * Props for the Combobox component
 * @extends ComboboxSingleProps<Item> | ComboboxMultipleProps<Item>
 */
export type ComboboxProps<Item = any> =
  | ComboboxSingleProps<Item>
  | ComboboxMultipleProps<Item>;

type SelectProps<Item = any> =
  | SelectSingleProps<Item>
  | SelectMultipleProps<Item>;

export type ComboboxSelectProps<Item> = SelectProps<Item>;

function isMultiple<Item>(
  props: ComboboxProps<Item> | SelectProps<Item>,
): props is ComboboxMultipleProps<Item> | SelectMultipleProps<Item> {
  return Object.hasOwn(props, "multiple") && props.multiple === true;
}

/**
 * ComboboxSingle component for single-item selection with search functionality.
 *
 * Features:
 * - Single item selection with search and filtering
 * - Configurable filtering options with match-sorter
 * - Optional "Add New" item functionality
 * - Grouping support with custom group labels and sorting
 * - Loading states with spinner indicators
 * - Disabled and read-only states
 * - Customizable popover behavior and positioning
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 *
 * @example
 * <Combobox.Select
 *   items={items}
 *   itemToString={(item) => item.name}
 *   onChange={setSelectedItem}
 *   filterOptions={{ keys: ["name"] }}
 * >
 *   <Combobox.SearchField label="Select an item" />
 *   <Combobox.Content>
 *     {({ items }) => (
 *       <Combobox.List>
 *         {items.map((item, i) => (
 *           <Combobox.Item key={item.id} item={item} index={i}>
 *             {item.name}
 *           </Combobox.Item>
 *         ))}
 *       </Combobox.List>
 *     )}
 *   </Combobox.Content>
 * </Combobox.Select>
 */
export const ComboboxSingle = function <Item>(
  props: ComboboxSingleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    filterOptions,
    disableFilter = false,
    loading = false,
    minPopoverHeight,
    root,
    onInputValueChange,
    environment: environmentProp,
    groupBy,
    groupToString,
    groupSorter,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { orderedItems: allItemsOrderedByGroup } = useGroups({
    items: itemsProp,
    groupBy,
    groupToString,
    groupSorter,
  });

  const { groups: searchedGroups, orderedItems: searchedItemsOrderedByGroup } =
    useGroups({
      items: items,
      groupBy,
      groupToString,
      groupSorter,
    });

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(
    () =>
      showAddNew
        ? [...searchedItemsOrderedByGroup, ADD_NEW as Item]
        : searchedItemsOrderedByGroup,
    [searchedItemsOrderedByGroup, showAddNew],
  );

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftCombobox<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // keep Combobox open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 250); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else {
            const forceClose = forceCloseOnSelectItems.includes(
              changes.selectedItem as Item,
            );

            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : forceClose
                    ? false
                    : disableCloseOnSelectItem
                      ? true
                      : changes.isOpen,
              highlightedIndex: disableCloseOnSelectItem
                ? itemToString(state.selectedItem) === state.inputValue ||
                  !state.inputValue
                  ? state.highlightedIndex
                  : 0
                : changes.highlightedIndex,
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftCombobox.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        default:
          return changes;
      }
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, type, selectedItem }) => {
        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type === useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftCombobox.stateChangeTypes.ItemClick
        ) {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        } else if (
          !inputValueMatchesSelectedItem &&
          !inputValueIsEmpty &&
          !disableFilter
        ) {
          setItems(
            matchSorter(allItemsOrderedByGroup, inputValue, filterOptions),
          );
          setHasExactMatch(
            matchSorter(allItemsOrderedByGroup, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        }
      }),
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    onIsOpenChange({ isOpen, inputValue, selectedItem }) {
      if (isOpen === false) {
        // reset the filtering whenever we close the dropdown
        // Delaying input reset timing to be after Popover close to avoid
        // list showing full version for a split second before closing
        setTimeout(() => {
          setItems(allItemsOrderedByGroup);

          if (inputValue === "") {
            // special case: if the search field is empty, remove the selected item
            selectItem(null);
          } else {
            // otherwise, force the text to match the selected item

            setInputValue(
              selectedItem != null ? itemToString(selectedItem) : "",
            );
          }
        }, 200);
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    setTimeout(() => {
      const inputValueMatchesSelectedItem =
        selectedItem != null && inputValue === itemToString(selectedItem);
      const inputValueIsEmpty = inputValue == null || inputValue === "";

      if (
        !inputValueMatchesSelectedItem &&
        !inputValueIsEmpty &&
        !disableFilter
      ) {
        setItems(
          matchSorter(allItemsOrderedByGroup, inputValue, filterOptions),
        );
        setHasExactMatch(
          matchSorter(allItemsOrderedByGroup, inputValue, {
            ...filterOptions,
            threshold: matchSorter.rankings.EQUAL,
          }).length > 0,
        );
      } else {
        setItems(allItemsOrderedByGroup);
        setHasExactMatch(true);
      }
    }, 200);
  }, [allItemsOrderedByGroup]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: false,
        select: false,
        items: searchedItemsOrderedByGroup,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getInputProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems: [],
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        getSelectedItemProps: undefined,
        getDropdownProps: undefined,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        hasAddNew,
        setHasAddNew,
        onSelectItemAddNew,
        setOnSelectItemAddNew,
        hasExactMatch,
        setHasExactMatch,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groupBy,
        groupToString,
        groups: searchedGroups,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

export const ComboboxMultiple = function <Item>(
  props: ComboboxMultipleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items: itemsProp,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    onStateChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    disableClearInputValueOnSelectItem = false,
    filterOptions,
    disableFilter = false,
    loading = false,
    minPopoverHeight,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    onInputValueChange,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    groupBy,
    groupToString,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [items, setItems] = useState(itemsProp);
  const [hasAddNew, setHasAddNew] = useState(false);
  const [onSelectItemAddNew, setOnSelectItemAddNew] = useState<
    | ((
        inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
      ) => void)
    | undefined
  >(undefined);
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [
    forceClearInputValueOnSelectItems,
    setForceClearInputValueOnSelectItems,
  ] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  // Downshift's useCombobox and useMultipleSelection have a kinda sorta
  // circular dependency, so storing setInputValue here to allow setting the
  // inputValue in the useMultipleSelection change handlers
  let storedSetInputValue: ((inputValue: string) => void) | undefined =
    undefined;

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const { orderedItems: allItemsOrderedByGroup } = useGroups({
    items: itemsProp,
    groupBy,
    groupToString,
  });

  const { groups: searchedGroups, orderedItems: searchedItemsOrderedByGroup } =
    useGroups({
      items: items,
      groupBy,
      groupToString,
    });

  const showAddNew = hasAddNew && !hasExactMatch;
  const combinedItems = useMemo(
    () =>
      showAddNew ? [...items, ADD_NEW as Item] : searchedItemsOrderedByGroup,
    [items, searchedItemsOrderedByGroup, showAddNew],
  );

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type } = changes;

      switch (type) {
        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          storedSetInputValue?.("");
          break;
      }

      onStateChange?.(changes);
    },
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    setInputValue,
    inputValue,
    closeMenu,
  } = useDownshiftCombobox<Item>({
    ...rest,
    items: combinedItems,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // keep Combobox open onBlur, will call close using Popover's onOutsidePress
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: state.inputValue,
            selectedItem: null,
            isOpen: open != null ? open : true,
            highlightedIndex: state.highlightedIndex,
          };

        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
          if (changes.selectedItem === ADD_NEW) {
            // if we're adding a new item, don't change the selectedItem
            // and just call the event handler
            // TODO: Figure out a way to do this without having side-effects in
            //       the stateReducer
            const stateInputValue = state.inputValue;
            setTimeout(() => {
              onSelectItemAddNew?.(stateInputValue);
            }, 250); // setTimeout so we don't setState during render

            return {
              ...changes,
              selectedItem: state.selectedItem,
              inputValue: "",
            };
          } else {
            const forceClose = forceCloseOnSelectItems.includes(
              changes.selectedItem as Item,
            );
            const forceClearInputValue =
              forceClearInputValueOnSelectItems.includes(
                changes.selectedItem as Item,
              );

            return {
              ...changes,
              isOpen:
                open != null
                  ? open
                  : forceClose
                    ? false
                    : disableCloseOnSelectItem
                      ? true
                      : changes.isOpen,
              highlightedIndex: forceClearInputValue
                ? 0
                : disableCloseOnSelectItem
                  ? disableClearInputValueOnSelectItem || !state.inputValue
                    ? state.highlightedIndex
                    : 0
                  : changes.highlightedIndex,
              inputValue: forceClearInputValue
                ? ""
                : disableClearInputValueOnSelectItem
                  ? state.inputValue
                  : "",
            };
          }

        // prevent this from closing the popover by default
        case useDownshiftCombobox.stateChangeTypes.InputClick:
          return { ...changes, isOpen: open != null ? open : true };

        // prevent this from clearing the input value by default
        case useDownshiftCombobox.stateChangeTypes
          .ControlledPropUpdatedSelectedItem:
          return { ...changes, inputValue: state.inputValue };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type, selectedItem: newSelectedItem } = changes;

      switch (type) {
        case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
        case useDownshiftCombobox.stateChangeTypes.ItemClick:
        case useDownshiftCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }

      onStateChange?.(changes);
    },
    onInputValueChange:
      onInputValueChange ??
      (({ inputValue, ...changes }) => {
        const { type, selectedItem } = changes;

        const inputValueMatchesSelectedItem =
          selectedItem != null && inputValue === itemToString(selectedItem);
        const inputValueIsEmpty = inputValue == null || inputValue === "";

        if (
          type === useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useDownshiftCombobox.stateChangeTypes.ItemClick
        ) {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        } else if (
          !inputValueMatchesSelectedItem &&
          !inputValueIsEmpty &&
          !disableFilter
        ) {
          setItems(
            matchSorter(allItemsOrderedByGroup, inputValue, filterOptions),
          );
          setHasExactMatch(
            matchSorter(allItemsOrderedByGroup, inputValue, {
              ...filterOptions,
              threshold: matchSorter.rankings.EQUAL,
            }).length > 0,
          );
        } else {
          setItems(allItemsOrderedByGroup);
          setHasExactMatch(true);
        }
      }),
    onIsOpenChange({ isOpen }) {
      if (isOpen === false) {
        // Delaying input reset timing to be after Popover close to avoid
        // list showing full version for a split second before closing
        setTimeout(() => {
          setInputValue("");

          setItems(allItemsOrderedByGroup);
        }, 200);
      }
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  // Now that useDownshiftCombobox has returned the setInputValue function,
  // we can store it here to call later inside of useDownshiftMultipleSelection
  storedSetInputValue = setInputValue;

  // prevent prop sync useEffects from running on intiial render
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  // synchronize filtering state when "items" prop changes
  useEffect(() => {
    if (firstRenderRef.current) return;

    setTimeout(() => {
      const inputValueIsEmpty = inputValue == null || inputValue === "";

      if (!inputValueIsEmpty && !disableFilter) {
        setItems(matchSorter(itemsProp, inputValue, filterOptions));
        setHasExactMatch(
          matchSorter(itemsProp, inputValue, {
            ...filterOptions,
            threshold: matchSorter.rankings.EQUAL,
          }).length > 0,
        );
      } else {
        setItems(itemsProp);
        setHasExactMatch(true);
      }
    }, 200);
  }, [itemsProp]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: true,
        select: false,
        items: searchedItemsOrderedByGroup,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getInputProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems,
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        getSelectedItemProps,
        getDropdownProps,
        reset,
        addSelectedItem,
        removeSelectedItem,
        setSelectedItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        hasAddNew,
        setHasAddNew,
        onSelectItemAddNew,
        setOnSelectItemAddNew,
        hasExactMatch,
        setHasExactMatch,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groupBy,
        groupToString,
        groups: searchedGroups,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

/**
 * NOTE: There is a lot of duplicated code here! Here's a list of the /actual/
 *       differences between select-only and regular combobox to help with
 *       refactoring this later!
 *
 *       1) Using Downshift's `useSelect` hook instead of `useCombobox`
 *          - Swapping out the stateChangeTypes for their equivalents
 *       2) Since there's no input, we don't need to passthrough `getInputProps`
 *       3) Since we don't do any sorting or filtering, we can remove:
 *          - onInputValueChange function and prop
 *            - setInputValue function
 *          - onIsOpenChange function
 *          - filterOptions prop
 *          - internal `items` state and useEffect to keep it in sync
 *            - useEffect to track first render
 *       4) Multiple-specific
 *          - Remove `disableClearInputValueOnSelectItem` prop
 *          - No need to store `setInputValue` function
 *          - Remove all state reducer references to `inputValue`
 *          - Remove `onStateChange` function from `useMultipleSelection`
 */

export const SelectSingle = function <Item>(props: SelectSingleProps<Item>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    disabled,
    readOnly,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    disableClearSelection = false,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    minPopoverHeight,
    root,
    environment: environmentProp,
    groupBy,
    groupToString,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const { groups, orderedItems } = useGroups({
    items,
    groupBy,
    groupToString,
  });

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items: orderedItems,
    itemToString,
    itemToKey,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : forceCloseOnSelectItems.includes(changes.selectedItem as Item)
                  ? false
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? state.highlightedIndex
              : changes.highlightedIndex,
          };
        default:
          return changes;
      }
    },
    onSelectedItemChange(changes) {
      const { selectedItem } = changes;

      if (onChange != null)
        onChange(selectedItem != null ? selectedItem : null);
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: false,
        select: true,
        disableClearSelection,
        items: orderedItems,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems: [],
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groups,
        groupBy,
        groupToString,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

export const SelectMultiple = function <Item>(
  props: SelectMultipleProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    className,
    style,
    children,
    open,
    items,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    onChange,
    onSelectedItemsChange,
    onStateChange,
    disableCloseOnClickOutside = false,
    disableMatchReferenceWidth = false,
    disableCloseOnSelectItem = false,
    loading = false,
    minPopoverHeight,
    root,
    disabled,
    readOnly,
    stateReducer: _,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp,
    groupBy,
    groupToString,
    ...rest
  } = componentProps;

  const ComboboxClassNames = cx(styles["combobox"], className);
  const [hasDisabledPopover, setHasDisabledPopover] = useState(false);
  const [hasEmpty, setHasEmpty] = useState(false);
  const [disabledItems, setDisabledItems] = useState<Item[]>([]);
  const [forceCloseOnSelectItems, setForceCloseOnSelectItems] = useState<
    Item[]
  >([]);
  const [, setForceClearInputValueOnSelectItems] = useState<Item[]>([]);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isReadOnly, setIsReadOnly] = useState(readOnly);
  /** @deprecated to be removed on next major - this is replaced by Combobox's disabled and readOnly props */
  const [isDisabled, setIsDisabled] = useState(disabled);

  const referenceRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleOutsidePress = (_e: MouseEvent) => {
    if (disableCloseOnClickOutside) return;
    closeMenu();
  };

  const { ref: wrapperDivRef, environment } = useDownshiftEnvironment();

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  const { groups, orderedItems } = useGroups({ items, groupBy, groupToString });

  const {
    getSelectedItemProps,
    getDropdownProps,
    reset,
    selectedItems,
    addSelectedItem,
    removeSelectedItem,
    setSelectedItems,
  } = useDownshiftMultipleSelection<Item>({
    itemToKey,
    initialSelectedItems,
    initialActiveIndex,
    defaultSelectedItems,
    defaultActiveIndex,
    activeIndex: activeIndexProp,
    selectedItems: selectedItemsProp,
    environment: environmentProp ?? environment,
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        // the Chip itself will handle "Backspace" and "Delete"
        // just return previous state
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownBackspace:
        case useDownshiftMultipleSelection.stateChangeTypes
          .SelectedItemKeyDownDelete:
          return state;

        case useDownshiftMultipleSelection.stateChangeTypes
          .FunctionRemoveSelectedItem:
          return { ...changes, activeIndex: -1 };

        case useDownshiftMultipleSelection.stateChangeTypes.FunctionReset:
          return { ...changes, selectedItems: [] };

        default:
          return changes;
      }
    },
    onStateChange,
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange != null) onChange(selectedItems);
      if (onSelectedItemsChange != null) onSelectedItemsChange(changes);
    },
  });

  const {
    isOpen,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    inputValue,
    closeMenu,
  } = useDownshiftSelect<Item>({
    ...rest,
    items: orderedItems,
    itemToString,
    itemToKey,
    selectedItem: null,
    isOpen: open,
    initialIsOpen: open,
    environment: environmentProp ?? environment,
    // NOTE: Reducer needs to be pure, side-effects should be in event handlers
    stateReducer(state, { type, changes }) {
      if (DEBUG_STATE_REDUCERS) console.log(type, changes, { state });

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen:
              open != null
                ? open
                : forceCloseOnSelectItems.includes(changes.selectedItem as Item)
                  ? false
                  : disableCloseOnSelectItem
                    ? true
                    : changes.isOpen,
            highlightedIndex: disableCloseOnSelectItem
              ? state.highlightedIndex
              : changes.highlightedIndex,
          };

        default:
          return changes;
      }
    },
    onStateChange(changes) {
      const { type, selectedItem: newSelectedItem } = changes;

      switch (type) {
        case useDownshiftSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useDownshiftSelect.stateChangeTypes.ItemClick:
        case useDownshiftSelect.stateChangeTypes.ToggleButtonBlur:
          if (newSelectedItem != null) {
            const itemAlreadySelected = selectedItems.some(
              (item) => itemToKey(item) === itemToKey(newSelectedItem),
            );

            if (itemAlreadySelected) {
              removeSelectedItem(newSelectedItem);
            } else {
              addSelectedItem(newSelectedItem);
            }
          }
          break;

        default:
          break;
      }

      onStateChange?.(changes);
    },
    isItemDisabled(item) {
      if (isDisabled || isReadOnly) {
        return !!isDisabled || !!isReadOnly;
      }

      if (
        item &&
        item instanceof Object &&
        "disabled" in item &&
        item.disabled
      ) {
        return true;
      }

      if (
        item &&
        item instanceof Object &&
        "readOnly" in item &&
        item.readOnly
      ) {
        return true;
      }

      if (disabledItems.includes(item)) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    setIsDisabled(disabled);
  }, [disabled]);

  useEffect(() => {
    setIsReadOnly(readOnly);
  }, [readOnly]);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: true,
        select: true,
        items: orderedItems,
        itemToString,
        itemToKey,
        isOpen,
        getLabelProps,
        getToggleButtonProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        selectedItem,
        selectedItems,
        disabledItems,
        setDisabledItems,
        setForceCloseOnSelectItems,
        setForceClearInputValueOnSelectItems,
        getSelectedItemProps,
        getDropdownProps,
        reset,
        addSelectedItem,
        removeSelectedItem,
        setSelectedItems,
        selectItem,
        inputValue,
        isDisabled,
        isReadOnly,
        setIsReadOnly,
        setIsDisabled,
        hasDisabledPopover,
        setHasDisabledPopover,
        hasEmpty,
        setHasEmpty,
        loading,
        referenceRef,
        contentRef,
        closeMenu,
        groupBy,
        groupToString,
        groups,
      }}
    >
      <div
        className={ComboboxClassNames}
        data-anv="combobox"
        style={{ ...style, ...layoutStyles }}
        ref={wrapperDivRef}
      >
        <Popover
          open={!isDisabled && isOpen}
          noPadding
          disableCaret
          fitScreen
          disableFlipFallback
          placement="bottom-start"
          matchReferenceWidth={!disableMatchReferenceWidth}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          onOutsidePress={handleOutsidePress}
          minHeight={minPopoverHeight}
        >
          {children}
        </Popover>
      </div>
    </ComboboxContext.Provider>
  );
};

// multi-select and single-select need to be separate components because
// we can't call hooks conditionally
export const ComboboxElement = function <Item>(props: ComboboxProps<Item>) {
  if (!supportsPopover()) {
    if (isMultiple(props)) {
      return <ComboboxLegacyMultiple<Item> {...props} />;
    } else {
      return <ComboboxLegacySingle<Item> {...props} />;
    }
  }

  if (isMultiple(props)) {
    return <ComboboxMultiple<Item> {...props} />;
  } else {
    return <ComboboxSingle<Item> {...props} />;
  }
};

export const ComboboxSelect = function <Item>(props: SelectProps<Item>) {
  if (!supportsPopover()) {
    if (isMultiple(props)) {
      return <SelectLegacyMultiple<Item> {...props} />;
    } else {
      return <SelectLegacySingle<Item> {...props} />;
    }
  }

  if (isMultiple(props)) {
    return <SelectMultiple<Item> {...props} />;
  } else {
    return <SelectSingle<Item> {...props} />;
  }
};

export const Combobox = Object.assign(ComboboxElement, {
  Select: ComboboxSelect,
  SearchField: ComboboxSearchField,
  SelectTrigger: ComboboxSelectTrigger,
  Content: ComboboxContent,
  List: ComboboxList,
  Item: ComboboxItem,
  ItemAddNew: ComboboxItemAddNew,
  Empty: ComboboxEmpty,
});
